import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { formatTemp, formatTempShort, getDayName, filterForecastDays, DEGREE_SYMBOL } from '../../utils/weatherUtils';
import Icon from '../../utils/icons';
import './TemperatureTrend.css';

function TemperatureTrend({ hourlyData, forecastData, activeLocation, historyData, onFetchHistory }) {
  const [viewMode, setViewMode] = useState('hourly');
  const [historyRange, setHistoryRange] = useState(2); // days: 1, 2, or 7
  const [historyLoading, setHistoryLoading] = useState(false);

  // Fetch history when switching to history mode or changing range
  useEffect(() => {
    if (viewMode === 'history' && activeLocation && onFetchHistory) {
      const cacheKey = `${activeLocation}:${historyRange}`;
      if (!historyData || !historyData[cacheKey]) {
        setHistoryLoading(true);
        onFetchHistory(activeLocation, historyRange).finally(() => {
          setHistoryLoading(false);
        });
      }
    }
  }, [viewMode, historyRange, activeLocation, onFetchHistory, historyData]);

  // Get the current history data for the selected location + range
  const currentHistory = useMemo(() => {
    if (!historyData || !activeLocation) return null;
    const cacheKey = `${activeLocation}:${historyRange}`;
    return historyData[cacheKey] || null;
  }, [historyData, activeLocation, historyRange]);

  // Process data based on view mode
  const chartData = useMemo(() => {
    if (viewMode === 'history' && currentHistory?.hours?.length > 0) {
      const hours = currentHistory.hours;
      // Downsample for display: show every Nth point depending on range
      const step = historyRange <= 1 ? 1 : historyRange <= 2 ? 1 : 3;
      const sampled = hours.filter((_, i) => i % step === 0);
      // Limit to reasonable chart points
      const maxPoints = 72;
      const display = sampled.length > maxPoints
        ? sampled.filter((_, i) => i % Math.ceil(sampled.length / maxPoints) === 0)
        : sampled;

      return display.map((h, index) => {
        const date = new Date(h.time);
        let label;
        if (historyRange <= 2) {
          label = date.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
        } else {
          label = date.toLocaleDateString('en-US', { weekday: 'short', hour: 'numeric', hour12: true });
        }
        return {
          label,
          temp: h.temperature,
          humidity: h.humidity,
          feelsLike: h.apparent_temperature,
          windSpeed: h.wind_speed,
          precipitation: h.precipitation,
          isHistory: true,
        };
      });
    } else if (viewMode === 'hourly' && hourlyData?.hours?.length > 0) {
      return hourlyData.hours.slice(0, 12).map((h, index) => ({
        label: index === 0 ? 'Now' : new Date(h.time).toLocaleTimeString('en-US', { 
          hour: 'numeric',
          hour12: true 
        }),
        temp: h.temperature,
        feelsLike: h.feels_like,
        humidity: h.humidity,
        condition: h.condition?.main,
        icon: h.condition?.icon
      }));
    } else if (viewMode === 'daily' && forecastData?.days?.length > 0) {
      const days = filterForecastDays(forecastData.days, 5);
      return days.map((d, index) => ({
        label: getDayName(d.date),
        temp: Math.round((d.temperature_max + d.temperature_min) / 2),
        high: d.temperature_max,
        low: d.temperature_min,
        humidity: d.humidity,
        condition: d.condition?.main,
        icon: d.condition?.icon
      }));
    }
    return [];
  }, [viewMode, hourlyData, forecastData, currentHistory, historyRange]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (chartData.length === 0) return null;

    const temps = viewMode === 'daily'
      ? chartData.flatMap(d => [d.high, d.low])
      : chartData.map(d => d.temp);

    const min = Math.min(...temps);
    const max = Math.max(...temps);
    const avg = temps.reduce((a, b) => a + b, 0) / temps.length;
    const trend = temps[temps.length - 1] - temps[0];

    // Humidity stats for history mode
    let humidityAvg = null;
    if (viewMode === 'history' && chartData.length > 0 && chartData[0].humidity !== undefined) {
      const humidities = chartData.map(d => d.humidity).filter(h => h != null);
      if (humidities.length > 0) {
        humidityAvg = Math.round(humidities.reduce((a, b) => a + b, 0) / humidities.length);
      }
    }

    return { min, max, avg, trend, humidityAvg };
  }, [chartData, viewMode]);

  // Chart configuration
  const chartConfig = useMemo(() => {
    if (!stats) return null;

    const padding = (stats.max - stats.min) * 0.15 || 5;
    const minY = stats.min - padding;
    const maxY = stats.max + padding;
    const range = maxY - minY;

    const getY = (temp) => {
      return 100 - ((temp - minY) / range) * 100;
    };

    const getX = (index) => {
      return (index / (chartData.length - 1)) * 100;
    };

    return { minY, maxY, range, getY, getX, padding };
  }, [stats, chartData.length]);

  // Generate SVG paths
  const paths = useMemo(() => {
    if (!chartConfig || chartData.length === 0) return {};

    const { getX, getY } = chartConfig;

    const tempLine = chartData.map((d, i) => {
      const x = getX(i);
      const y = getY(d.temp);
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');

    const tempArea = `${tempLine} L 100 100 L 0 100 Z`;

    let highLine = '';
    let lowLine = '';
    let humidityLine = '';
    
    if (viewMode === 'daily') {
      highLine = chartData.map((d, i) => {
        const x = getX(i);
        const y = getY(d.high);
        return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
      }).join(' ');

      lowLine = chartData.map((d, i) => {
        const x = getX(i);
        const y = getY(d.low);
        return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
      }).join(' ');
    }

    // Humidity line for history mode (scaled to chart Y range)
    if (viewMode === 'history' && chartData.some(d => d.humidity != null)) {
      const humidities = chartData.map(d => d.humidity);
      const humMin = Math.min(...humidities);
      const humMax = Math.max(...humidities);
      const humRange = humMax - humMin || 1;

      humidityLine = chartData.map((d, i) => {
        const x = getX(i);
        // Map humidity 0-100% to chart y-space
        const y = 100 - ((d.humidity - humMin) / humRange) * 80 - 10;
        return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
      }).join(' ');
    }

    return { tempLine, tempArea, highLine, lowLine, humidityLine };
  }, [chartData, chartConfig, viewMode]);

  // Handle view mode change
  const handleViewChange = useCallback((mode) => {
    setViewMode(mode);
  }, []);

  // Handle history range change
  const handleRangeChange = useCallback((days) => {
    setHistoryRange(days);
  }, []);

  if (chartData.length === 0 && viewMode !== 'history') {
    return (
      <div className="temperature-trend">
        <div className="card-header">
          <h2 className="card-title">
            <Icon name="activity" size={18} /> Temperature Trend
          </h2>
        </div>
        <div className="no-data">
          <Icon name="activity" size={48} className="no-data-icon" />
          <p>No temperature data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="temperature-trend">
      <div className="card-header">
        <div className="title-section">
          <h2 className="card-title">
            <Icon name="activity" size={18} /> Temperature Trend
          </h2>
          <span className="card-subtitle">
            {viewMode === 'hourly' ? 'Next 12 hours' :
             viewMode === 'daily' ? '5-day overview' :
             `Past ${historyRange === 1 ? '24 hours' : historyRange === 2 ? '48 hours' : '7 days'}`}
          </span>
        </div>

        <div className="view-toggle">
          <button
            className={`toggle-btn ${viewMode === 'hourly' ? 'active' : ''}`}
            onClick={() => handleViewChange('hourly')}
          >
            Hourly
          </button>
          <button
            className={`toggle-btn ${viewMode === 'daily' ? 'active' : ''}`}
            onClick={() => handleViewChange('daily')}
          >
            Daily
          </button>
          <button
            className={`toggle-btn ${viewMode === 'history' ? 'active' : ''}`}
            onClick={() => handleViewChange('history')}
          >
            History
          </button>
        </div>
      </div>

      {/* History range selector */}
      {viewMode === 'history' && (
        <div className="history-range-selector">
          <button
            className={`range-btn ${historyRange === 1 ? 'active' : ''}`}
            onClick={() => handleRangeChange(1)}
          >
            24h
          </button>
          <button
            className={`range-btn ${historyRange === 2 ? 'active' : ''}`}
            onClick={() => handleRangeChange(2)}
          >
            48h
          </button>
          <button
            className={`range-btn ${historyRange === 7 ? 'active' : ''}`}
            onClick={() => handleRangeChange(7)}
          >
            7 days
          </button>
        </div>
      )}

      {/* Loading state for history */}
      {viewMode === 'history' && historyLoading && (
        <div className="history-loading">
          <div className="spinner small"></div>
          <span>Loading historical data...</span>
        </div>
      )}

      {/* Statistics Cards */}
      {stats && chartData.length > 0 && (
        <div className="stats-row">
          <div className="stat-card">
            <Icon name="arrowUp" size={20} className="stat-icon high" />
            <div className="stat-info">
              <span className="stat-value">{formatTemp(stats.max)}</span>
              <span className="stat-label">High</span>
            </div>
          </div>
          <div className="stat-card">
            <Icon name="arrowDown" size={20} className="stat-icon low" />
            <div className="stat-info">
              <span className="stat-value">{formatTemp(stats.min)}</span>
              <span className="stat-label">Low</span>
            </div>
          </div>
          {viewMode === 'history' && stats.humidityAvg !== null ? (
            <div className="stat-card">
              <Icon name="droplet" size={20} className="stat-icon humidity" />
              <div className="stat-info">
                <span className="stat-value">{stats.humidityAvg}%</span>
                <span className="stat-label">Avg Humidity</span>
              </div>
            </div>
          ) : (
            <div className="stat-card">
              <Icon name="activity" size={20} className="stat-icon" />
              <div className="stat-info">
                <span className="stat-value">{formatTemp(stats.avg)}</span>
                <span className="stat-label">Average</span>
              </div>
            </div>
          )}
          <div className={`stat-card trend ${stats.trend >= 0 ? 'rising' : 'falling'}`}>
            <Icon 
              name={stats.trend >= 0 ? 'trendingUp' : 'trendingDown'} 
              size={20} 
              className="stat-icon" 
            />
            <div className="stat-info">
              <span className="stat-value">
                {stats.trend >= 0 ? '+' : ''}{Math.round(stats.trend)}{DEGREE_SYMBOL}
              </span>
              <span className="stat-label">Trend</span>
            </div>
          </div>
        </div>
      )}

      {/* Chart Container */}
      {chartData.length > 0 && chartConfig && (
        <div className="chart-container">
          <div className="y-axis">
            <span className="y-label">{formatTemp(stats.max)}</span>
            <span className="y-label">{formatTemp(stats.avg)}</span>
            <span className="y-label">{formatTemp(stats.min)}</span>
          </div>

          <div className="chart-area">
            <svg 
              viewBox="0 0 100 100" 
              preserveAspectRatio="none"
              className="chart-svg"
            >
              <defs>
                <linearGradient id="tempGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="var(--accent-orange)" stopOpacity="0.4" />
                  <stop offset="50%" stopColor="var(--accent-cyan)" stopOpacity="0.2" />
                  <stop offset="100%" stopColor="var(--accent-blue)" stopOpacity="0.05" />
                </linearGradient>
                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="var(--accent-blue)" />
                  <stop offset="50%" stopColor="var(--accent-cyan)" />
                  <stop offset="100%" stopColor="var(--accent-orange)" />
                </linearGradient>
              </defs>

              <g className="grid-lines">
                <line x1="0" y1="25" x2="100" y2="25" />
                <line x1="0" y1="50" x2="100" y2="50" />
                <line x1="0" y1="75" x2="100" y2="75" />
              </g>

              <path
                d={paths.tempArea}
                fill="url(#tempGradient)"
                className="area-path"
              />

              {viewMode === 'daily' && paths.highLine && (
                <path
                  d={paths.highLine}
                  fill="none"
                  stroke="var(--accent-orange)"
                  strokeWidth="0.8"
                  strokeDasharray="2,2"
                  className="high-line"
                />
              )}

              {viewMode === 'daily' && paths.lowLine && (
                <path
                  d={paths.lowLine}
                  fill="none"
                  stroke="var(--accent-blue)"
                  strokeWidth="0.8"
                  strokeDasharray="2,2"
                  className="low-line"
                />
              )}

              {/* Humidity line for history mode */}
              {viewMode === 'history' && paths.humidityLine && (
                <path
                  d={paths.humidityLine}
                  fill="none"
                  stroke="var(--accent-green)"
                  strokeWidth="0.8"
                  strokeDasharray="3,2"
                  opacity="0.7"
                  className="humidity-line"
                />
              )}

              <path
                d={paths.tempLine}
                fill="none"
                stroke="url(#lineGradient)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="temp-line"
              />

              {/* Only show data points when there aren't too many */}
              {chartData.length <= 24 && chartData.map((d, i) => {
                const x = chartConfig.getX(i);
                const y = chartConfig.getY(d.temp);
                return (
                  <g key={i} className="data-point-group">
                    <circle
                      cx={x}
                      cy={y}
                      r="3"
                      fill="var(--accent-cyan)"
                      opacity="0.3"
                      className="point-glow"
                    />
                    <circle
                      cx={x}
                      cy={y}
                      r="1.5"
                      fill="var(--text-primary)"
                      stroke="var(--accent-cyan)"
                      strokeWidth="0.5"
                      className="data-point"
                    />
                  </g>
                );
              })}
            </svg>

            {/* X-axis labels — show fewer labels for history mode */}
            <div className="x-axis">
              {chartData
                .map((d, i) => ({ ...d, _index: i }))
                .filter((d, i, arr) => {
                  if (viewMode === 'history') {
                    // Show max 6 labels evenly spaced
                    const maxLabels = 6;
                    const step = Math.max(1, Math.floor(arr.length / maxLabels));
                    return i % step === 0;
                  }
                  // For hourly/daily, show max 8 labels
                  const step = Math.max(1, Math.floor(arr.length / 8));
                  return i % step === 0;
                })
                .map((d, i) => (
                  <div key={i} className="x-label-container">
                    <span className="x-label">{d.label}</span>
                    <span className="x-temp">{formatTempShort(d.temp)}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      <div className="chart-legend">
        <div className="legend-item">
          <span className="legend-line main"></span>
          <span className="legend-text">
            {viewMode === 'hourly' ? 'Temperature' : viewMode === 'daily' ? 'Average' : 'Temperature'}
          </span>
        </div>
        {viewMode === 'daily' && (
          <>
            <div className="legend-item">
              <span className="legend-line high"></span>
              <span className="legend-text">High</span>
            </div>
            <div className="legend-item">
              <span className="legend-line low"></span>
              <span className="legend-text">Low</span>
            </div>
          </>
        )}
        {viewMode === 'history' && (
          <div className="legend-item">
            <span className="legend-line humidity"></span>
            <span className="legend-text">Humidity</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default TemperatureTrend;
